/**
 * The Needs Cleaned meeting-date cutoff, as it travels (sc-1212).
 *
 * Four representations, each used in exactly one place:
 *
 * - `YYYY-MM-DD` - the URL param AND the API param. A Denver calendar date.
 * - `all` ({@link CLEANING_SINCE_ALL}) - the URL param only: no cutoff, the
 *   full audit. It never reaches the API; {@link effectiveCleaningSince} turns
 *   it into `undefined`. Deliberately not JSON-parseable, so the router writes
 *   it bare rather than as `%22all%22` (same reason the flags use `'on'`).
 * - absent - the URL param only: the default, 1 January of the current Denver
 *   year ({@link defaultCleaningSince}), computed at render so it rolls over
 *   every January by itself. Never written to the URL.
 * - `YYYY-MM-DDT12:00:00.000Z` - what `DatePickerField` takes and emits. The
 *   noon is deliberate on its side (no timezone can move a noon-UTC instant to
 *   another calendar day), so the date part of that string IS the picked day.
 *
 * The conversion lives here, once, so the `T12:00:00.000Z` form never reaches
 * the URL or the request.
 */

import { REPORTING_TIME_ZONE } from '@/lib/formatters'

/** The explicit "no cutoff" URL value. Not JSON-parseable on purpose. */
export const CLEANING_SINCE_ALL = 'all'

/**
 * 1 January of the current year in the reporting zone (Denver), as
 * `YYYY-01-01`. The year is read in Denver, not the viewer's zone, so it does
 * not flip early for anyone east of Denver around New Year.
 */
export function defaultCleaningSince(now: Date = new Date()): string {
  const year = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORTING_TIME_ZONE,
    year: 'numeric',
  }).format(now)
  return `${year}-01-01`
}

/**
 * The date to send to the server for a URL param: the default when absent,
 * `undefined` (no cutoff) for `all`, otherwise the date itself.
 */
export function effectiveCleaningSince(
  param: string | undefined,
  now: Date = new Date(),
): string | undefined {
  if (param === CLEANING_SINCE_ALL) return undefined
  return param ?? defaultCleaningSince(now)
}

const SINCE_SHAPE = /^(\d{4})-(\d{2})-(\d{2})$/

/** True for a real calendar date written `YYYY-MM-DD` (2026-02-31 is not). */
export function isCleaningSince(value: string): boolean {
  const match = SINCE_SHAPE.exec(value)
  if (!match) return false
  const [year, month, day] = [match[1], match[2], match[3]].map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  )
}

/** The picker's value for a cutoff; null when there is none. */
export function sinceToPickerValue(since: string | undefined): string | null {
  return since && isCleaningSince(since) ? `${since}T12:00:00.000Z` : null
}

/** The cutoff for a picker value; undefined when cleared or not a date. */
export function pickerValueToSince(value: string | null): string | undefined {
  const day = value?.slice(0, 10)
  return day && isCleaningSince(day) ? day : undefined
}
